import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchBlogPosts } from '../../contentful';
import {
  createAnimatedBackground,
  createMotionProps,
} from '../../utils/animations/headerAnimations';

const BlogHeader = () => {
  const backgroundElements = createAnimatedBackground();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAndProcessData = async () => {
      try {
        const rawData = await fetchBlogPosts();
        if (rawData && rawData.length > 0) {
          setPosts(rawData);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessData();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = posts.filter(post => {
      const title = post.fields.title.toLowerCase();
      const excerpt = post.fields.excerpt?.toLowerCase() || '';
      const tags = post.fields.tags?.map(tag => tag.toLowerCase()) || [];
      
      return title.includes(query) || 
             excerpt.includes(query) || 
             tags.some(tag => tag.includes(query));
    });

    // Show only 1-3 results
    setSearchResults(results.slice(0, 3));
  }, [searchQuery, posts]);

  return (
    <motion.section
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={{ clipPath: "inset(0 0 0 0)" }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      className="relative w-full bg-gradient-to-b from-[#D3242A] to-[#B31E23]"
      style={{
        minHeight: searchResults.length > 0 ? 'auto' : '65vh',
        overflow: 'visible'
      }}
    >
      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 pt-20 md:pt-32 pb-4 md:pb-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            {...createMotionProps()}
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 uppercase tracking-tight"
            style={{ fontFamily: "Geogrotesque, sans-serif" }}
          >
            Tips & Articles
          </motion.h1>
          
          <motion.div
            {...createMotionProps(0.2)}
            className="relative inline-block"
          >
            <p className="relative text-xl md:text-2xl text-white/90 font-medium px-8 py-4">
              Expert insights and professional cleaning tips
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            {...createMotionProps(0.3)}
            className="mt-8 max-w-2xl mx-auto relative z-50"
          >
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full px-6 py-4 text-lg bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-full text-white placeholder-white/60 focus:outline-none focus:border-white/40 transition-colors"
              />
              <svg
                className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </motion.div>

          {/* Search Results */}
          <AnimatePresence>
            {searchResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {searchResults.map((result) => (
                  <Link
                    key={result.sys.id}
                    to={`/tips-and-articles/${result.fields.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden border border-white/20 hover:bg-white/20 transition-all duration-200"
                    >
                      {result.fields.featuredImage?.fields?.file?.url && (
                        <div className="aspect-w-16 aspect-h-9">
                          <img
                            src={result.fields.featuredImage.fields.file.url}
                            alt={result.fields.title}
                            className="w-full h-48 object-cover"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {result.fields.title}
                        </h3>
                        {result.fields.excerpt && (
                          <p className="text-sm text-white/80 line-clamp-2 mb-3">
                            {result.fields.excerpt}
                          </p>
                        )}
                        {result.fields.tags && result.fields.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {result.fields.tags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="text-xs bg-white/20 text-white px-2 py-1 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bubbles moved to BlogBubbles.jsx for later use */}
        </div>
      </div>

      {/* Decorative Bottom Wave */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 10 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className="absolute bottom-0 left-0 right-0"
        style={{ overflow: 'visible' }}
      >
        <div className="relative w-full" style={{ height: '122px', overflow: 'visible' }}>
          <svg
            viewBox="0 0 1440 120"
            fill="white"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute bottom-0 left-0 w-full h-full"
            preserveAspectRatio="none"
            style={{ transform: 'scale(1.5) translateY(3px)' }}
          >
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z"
            />
          </svg>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default BlogHeader; 